import { FC, useCallback, useEffect, useState } from 'react'
import { Autocomplete, TextField } from '@mui/material'
import { Account, useFetchAllAccountsLazyQuery } from '../../../generated'

import { GRAPHQL_QUERY_POLICY_CACHE_FIRST, PAGE_LIMIT } from '../../constants'
import { useDebounce } from 'use-debounce'
import { AccountOption } from '../../interfaceTypes/common'
import { AccountDropdownProps } from '../../interfaceTypes/props'

const AccountDropdown: FC<AccountDropdownProps> = ({
  searchFilters,
  disabled,
  value,
  onChange,
  errorMessage,
  label = 'Account',
  setBalance,
  sx,
  required = false,
  autoSelectOnTab = true
}) => {
  const [accounts, setAccounts] = useState<AccountOption[]>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [page, setPage] = useState<number>(1)

  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)

  // Accounts with full account object instead of just label and value
  const [fullAccounts, setFullAccounts] = useState<Account[]>([])

  const [fetchAccounts, { loading }] = useFetchAllAccountsLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    variables: undefined,
    onCompleted(data) {
      const fetchedAccounts = (data?.fetchAllAccounts?.accounts ?? []).filter(
        (account): account is Account => account !== null
      )
      if (fetchedAccounts.length) {
        if (page > 1) setFullAccounts(prev => [...prev, ...fetchedAccounts])
        else setFullAccounts(fetchedAccounts)

        if (page > 1) {
          const newOffices = fetchedAccounts.map(account => ({
            value: account?.id || '',
            label: `${account?.sortCode ? `(#${account?.sortCode})` : ''} ${account?.name || ''}`
          }))
          setAccounts(prev => [...prev, ...newOffices])
        } else
          setAccounts(
            fetchedAccounts.map(account => ({
              value: account?.id || '',
              label: `${account?.sortCode ? `(#${account?.sortCode})` : ''} ${account?.name || ''}`,
              data: account
            }))
          )
      }
    }
  })

  useEffect(() => {
    setPage(1)
  }, [searchTerm])

  useEffect(() => {
    fetchAccounts({
      variables: {
        accountsInput: {
          ...searchFilters,
          searchValue: debouncedSearchTerm,
          paginationOptions: {
            page: page,
            limit: PAGE_LIMIT
          }
        }
      }
    })
  }, [debouncedSearchTerm, fetchAccounts, searchFilters, page])

  const handleAccountChange = (option: AccountOption | null) => {
    onChange(option)
    if (setBalance && option) {
      // Find the selected account in the fullAccounts array
      const selectedAccount = fullAccounts.find(acc => acc?.id === option.value)
      // Try to get the balance, fallback to 0.00 if not found
      const balance =
        selectedAccount &&
        typeof selectedAccount.currentBalance !== 'undefined' &&
        selectedAccount.currentBalance !== null
          ? Number(selectedAccount.currentBalance).toFixed(2)
          : '0.00'
      setBalance(balance)
    } else if (setBalance && !option) {
      setBalance('0.00')
    }
  }

  const handleScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const listboxNode = event.currentTarget
      if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 && !loading) {
        setPage(prev => prev + 1)
      }
    },
    [loading]
  )

  useEffect(() => {
    if (value && value.value && !accounts.find(account => account.value === value.value))
      setAccounts(prev => [value, ...prev])
  }, [accounts, value])

  return (
    <Autocomplete
      sx={sx ?? { minWidth: 250 }}
      options={accounts}
      value={value ?? null}
      loading={loading}
      onChange={(_, value) => handleAccountChange(value)}
      onInputChange={(_, newInputValue) => setSearchTerm(newInputValue)}
      getOptionLabel={option => option.label}
      disabled={disabled}
      autoHighlight={autoSelectOnTab}
      autoSelect={autoSelectOnTab}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          required={required}
          label={label}
          error={!!errorMessage}
          helperText={errorMessage}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.8125rem'
            }
          }}
        />
      )}
      ListboxProps={{
        role: 'list-box',
        onScroll: handleScroll
      }}
    />
  )
}

export default AccountDropdown
